/**
 * outbound (출고) orval 래퍼
 */
import { customInstance } from "../custom-instance";
import { DefaultApprovalStatusSchema, } from "./default-schemes";
import { validateArrayWithSchema, validateWithSchema, } from "@erp/shared/utils/https/zod-validation";
import z from "zod";
const OUTBOUND_URLS = {
    commercial: {
        confirm: "/outbound-confirm/commercial",
        products: (confirmId) => `/stock-transaction/commercial/outbound/${confirmId}/products`,
        productAll: "/stock-transaction/commercial/outbound",
    },
    research: {
        confirm: "/outbound-confirm/research",
        products: (confirmId) => `/stock-transaction/research/outbound/${confirmId}/products`,
        productAll: "/stock-transaction/research/outbound",
    },
};
// ── 스키마 & 타입 (OutboundConfirm) ──
export const GetOutboundConfirmResponseSchema = z
    .object({
    ...DefaultApprovalStatusSchema.shape,
    id: z.number(),
    addData: z.string().nullable(),
    projectId: z.coerce.number().nullable(),
    projectCode: z.string().nullable(),
    outboundDate: z.string().datetime().nullable(),
    outboundPlace: z.string().nullable(),
    outboundSpotId: z.number().nullable().optional(),
    outboundSpotName: z.string().nullable().optional(),
    purpose: z.string().nullable(),
    note: z.string().nullable(),
    inspectionReportFile: z.string().nullable(),
    etcFiles: z.array(z.string()).nullable(),
})
    .strict();
// ── 스키마 & 타입 (OutboundProducts) ──
export const GetOutboundProductResponseSchema = z
    .object({
    ...DefaultApprovalStatusSchema.shape,
    id: z.number(), spotId: z.number(), num: z.number(), unitPrice: z.number(),
    spot: z.string().nullable(), productId: z.number(),
    type: z.string().nullable(), productCode: z.string(),
    name: z.string().nullable(), spec: z.string().nullable(),
    model: z.string().nullable(), manufacturerId: z.number(),
    manufacturer: z.string().nullable(), classificationId: z.number(),
    classification: z.string().nullable(), orderId: z.number().nullable(),
    outboundConfirmId: z.number(), inboundId: z.number().nullable(),
    outboundDate: z.string().datetime(), outboundPlace: z.string().nullable(),
    outboundSpotId: z.number().nullable().optional(),
    outboundSpotName: z.string().nullable().optional(),
    purpose: z.string().nullable(), inspectionReportFile: z.string().nullable(),
    etcFiles: z.array(z.string()).nullable(), note: z.string().nullable(),
    addData: z.string().nullable(), projectId: z.coerce.number().nullable(),
    projectCode: z.string().nullable(), productTotalStockNum: z.number(),
})
    .strict();
// ── 스키마 & 타입 (OutboundProductAll) ──
export const GetOutboundProductAllResponseSchema = z
    .object({
    id: z.coerce.number(), spotId: z.coerce.number(), num: z.number(),
    unitPrice: z.number(), spot: z.string().nullable(),
    productId: z.coerce.number(), type: z.string().nullable(),
    productCode: z.string(), name: z.string().nullable(),
    spec: z.string().nullable(), model: z.string().nullable(),
    manufacturerId: z.coerce.number(), manufacturer: z.string().nullable(),
    classificationId: z.coerce.number(), classification: z.string().nullable(),
    orderId: z.coerce.number().nullable(), outboundConfirmId: z.coerce.number(),
    inboundId: z.coerce.number().nullable(), outboundDate: z.string(),
    outboundPlace: z.string().nullable(),
    outboundSpotId: z.number().nullable().optional(),
    outboundSpotName: z.string().nullable().optional(),
    purpose: z.string().nullable(),
    inspectionReportFile: z.string().nullable(),
    etcFiles: z.array(z.string()).nullable(),
    note: z.string().nullable(), addData: z.string().nullable(),
    createdTime: z.string(), modifiedTime: z.string(),
    modifier: z.coerce.number(),
    approvalPhase: z.string(), creater: z.coerce.number(),
    checker: z.coerce.number(), checkedDate: z.string().nullable(),
    approver: z.coerce.number(), approvedDate: z.string().nullable(),
    rejecter: z.coerce.number().nullable(), rejectedDate: z.string().nullable(),
    rejectReason: z.string().nullable(),
    projectId: z.number().nullable(), projectCode: z.string().nullable(),
    createrId: z.string(), createrName: z.string(),
    checkerId: z.string().nullable(), checkerName: z.string().nullable(),
    approverId: z.string().nullable(), approverName: z.string().nullable(),
    rejecterId: z.string().nullable(), rejecterName: z.string().nullable(),
})
    .strict();
// ── API 함수 ──
function createGetOutboundConfirm(type) {
    return async (params) => {
        const data = await customInstance({
            url: OUTBOUND_URLS[type].confirm, method: "GET", params,
        });
        return validateArrayWithSchema(GetOutboundConfirmResponseSchema, data);
    };
}
export const getCommercialOutboundConfirm = createGetOutboundConfirm("commercial");
export const getResearchOutboundConfirm = createGetOutboundConfirm("research");
function createPostOutboundConfirm(type) {
    return async (body) => {
        return customInstance({
            url: OUTBOUND_URLS[type].confirm, method: "POST",
            headers: { "Content-Type": "application/json" }, data: body,
        });
    };
}
export const postCommercialOutboundConfirm = createPostOutboundConfirm("commercial");
export const postResearchOutboundConfirm = createPostOutboundConfirm("research");
function createPatchOutboundApprove(type) {
    return async ({ pathParams, body }) => {
        return customInstance({
            url: `${OUTBOUND_URLS[type].confirm}/${pathParams.segment}/${pathParams.outboundId}`,
            method: "PATCH", headers: { "Content-Type": "application/json" }, data: body,
        });
    };
}
export const patchCommercialOutboundApprove = createPatchOutboundApprove("commercial");
export const patchResearchOutboundApprove = createPatchOutboundApprove("research");
function createGetOutboundProducts(type) {
    return async (outboundId) => {
        const data = await customInstance({
            url: OUTBOUND_URLS[type].products(outboundId), method: "GET",
        });
        return validateArrayWithSchema(GetOutboundProductResponseSchema, data);
    };
}
export const getCommercialOutboundProducts = createGetOutboundProducts("commercial");
export const getResearchOutboundProducts = createGetOutboundProducts("research");
function createGetOutboundProductAll(type) {
    return async (params) => {
        const data = await customInstance({
            url: OUTBOUND_URLS[type].productAll, method: "GET",
            params: { ...params, noInitial: true },
        });
        return validateArrayWithSchema(GetOutboundProductAllResponseSchema, data);
    };
}
export const getCommercialOutboundProductAll = createGetOutboundProductAll("commercial");
export const getResearchOutboundProductAll = createGetOutboundProductAll("research");
export async function getOutboundConfirmDetail(id) {
    const data = await customInstance({
        url: `/outbound-confirm/commercial/${id}`, method: "GET",
    });
    return validateWithSchema(GetOutboundConfirmResponseSchema, data);
}
// ── SDK 객체 ──
export const outboundApi = {
    getCommercialOutboundConfirm,
    getResearchOutboundConfirm,
    postCommercialOutboundConfirm,
    postResearchOutboundConfirm,
    patchCommercialOutboundApprove,
    patchResearchOutboundApprove,
    getCommercialOutboundProducts,
    getResearchOutboundProducts,
    getCommercialOutboundProductAll,
    getResearchOutboundProductAll,
    getOutboundConfirmDetail,
};
